import { Button, Flex, BoxProps } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useUserData } from "../../hooks/useUserData";
import {
  ContentSectionCustomCard,
  Section,
  ReadStatus,
  Release,
  Bundle,
} from "../../types";
import { useMobileBreakpoint } from "../../utils/useBreakpoints";
import {
  FunnelConfigTypes,
  FunnelContextArea,
  FunnelOnSuccessResult,
  EventContextArea,
} from "../funnel/types";
import { FUNNEL_TYPES } from "../funnel/utils/contants";
import { ReadingStatusButton } from "../readingStatusButton/ReadingStatusButton";
import { useSlideInMenu } from "../slideInMenu/hooks/useSlideInMenu";
import { CONTEXT_ENTITY_TYPES } from "./constants";
import { ReleaseCardMenu } from "./ReleaseCardMenu";
import { setFunnelModalConfig } from "../funnel/FunnelModal.tsx";
import { doesUserHaveAccess } from "../../utils/doesUserHaveAccess.ts";
import { ReleasePricingActions } from "./ReleasePricingActions";
import { useReleasePricingInfo } from "../../hooks/useReleasePricingInfo.ts";
import { trackRecsysCardItemClick } from "../../analytics/recsys/tracking";
import { TFunction } from "i18next";
import { ReaderNavigationContext } from "../../screens/reader/types";
import { applyNavigationAnalyticsAttribution } from "../../analytics/params/navigationContextAttribution";

interface FeatureCardALCButtonsProps extends BoxProps {
  contentCard: ContentSectionCustomCard;
  isHidden?: boolean;
  isRoadblock?: boolean;
  index?: number;
  sectionIndex?: number;
  section?: Section;
  buttonWidth?: string | Record<string, string>;
  goToCTAUrl: () => void;
  goToCTAUrlWithContext: (overrides?: Partial<ReaderNavigationContext>) => void;
  navigationContext?: ReaderNavigationContext;
}

export function FeatureCardALCButtons({
  contentCard,
  isHidden = false,
  isRoadblock = false,
  goToCTAUrl,
  index,
  sectionIndex,
  section,
  buttonWidth,
  navigationContext,
  goToCTAUrlWithContext,
  ...props
}: FeatureCardALCButtonsProps) {
  const isMobile = useMobileBreakpoint();
  const isComicCard =
    contentCard.context_entity_type_id === CONTEXT_ENTITY_TYPES.comic;

  const { userData } = useUserData();
  const userIsGold = userData?.gold ?? false;
  const { openReleaseInfoMenu, openBundleDetails } = useSlideInMenu();

  const releaseData =
    !isComicCard && contentCard.release ? contentCard.release : undefined;
  const comicDataToUse =
    isComicCard && contentCard.comic
      ? contentCard.comic
      : contentCard.release?.comic;
  const bundleData = contentCard.bundle;

  const handleSubscriptionSuccess = (result: FunnelOnSuccessResult) => {
    setFunnelModalConfig({
      type: FUNNEL_TYPES.congrats,
      context: result.context,
      options: {
        ...result.options,
        onSuccess: () => setFunnelModalConfig(),
      },
    });
  };

  const handleFunnelSuccess = (
    result: FunnelOnSuccessResult,
    metadata?: {
      nextType: FUNNEL_TYPES;
    },
  ) => {
    const release = result.data?.[1]?.data?.payload?.results;
    const { userHasAccess } = doesUserHaveAccess(result.data);
    const { nextType } = metadata || {};
    if (userHasAccess) {
      setFunnelModalConfig({
        type:
          nextType === FUNNEL_TYPES.purchaseRelease
            ? FUNNEL_TYPES.purchaseRelease
            : FUNNEL_TYPES.goldAccess,
        context: {
          ...result.context,
          purchaseRelease: release as Release,
        },
        options: {
          ...result.options,
          onSuccess: () => setFunnelModalConfig(),
        },
      });
    } else {
      setFunnelModalConfig({
        type: nextType,
        context: result.context,
        options: {
          ...result.options,
          onSuccess:
            nextType === FUNNEL_TYPES.subscription
              ? (result) => handleSubscriptionSuccess(result)
              : () => setFunnelModalConfig(),
        },
      });
    }
  };

  const handlePurchaseClick = (config: FunnelConfigTypes) => {
    const context = {
      ...config.context,
      modalContext: FunnelContextArea.featureCard,
    };

    if (userData) {
      setFunnelModalConfig({
        type: FUNNEL_TYPES.purchaseRelease,
        context,
        options: {
          onSuccess: () => setFunnelModalConfig(),
        },
      });
    } else {
      setFunnelModalConfig({
        type: FUNNEL_TYPES.signup,
        context,
        options: {
          skipOnboarding: true,
          onSuccess: (result) => {
            handleFunnelSuccess(result, {
              nextType: FUNNEL_TYPES.purchaseRelease,
            });
          },
        },
      });
    }
  };

  const handleGoldPaywallClick = (config: FunnelConfigTypes) => {
    const context = {
      ...config.context,
      modalContext: FunnelContextArea.featureCard,
    };
    if (userData) {
      setFunnelModalConfig({
        type: FUNNEL_TYPES.subscription,
        context,
        options: {
          onSuccess: handleSubscriptionSuccess,
        },
      });
    } else {
      setFunnelModalConfig({
        type: FUNNEL_TYPES.signup,
        context,
        options: {
          skipOnboarding: true,
          onSuccess: (result) => {
            handleFunnelSuccess(result, {
              nextType: FUNNEL_TYPES.subscription,
            });
          },
        },
      });
    }
  };

  const handleButtonClick = (config?: FunnelConfigTypes) => {
    const impressionId =
      section && sectionIndex !== undefined
        ? trackRecsysCardItemClick({
            section,
            sectionIndex,
            item: contentCard,
            itemIndex: index ?? 0,
          })
        : undefined;

    if (config) {
      if (config.type === FUNNEL_TYPES.purchaseRelease) {
        handlePurchaseClick(config);
        return;
      } else if (config.type === FUNNEL_TYPES.subscription) {
        handleGoldPaywallClick(config);
        return;
      } else if (config.type === FUNNEL_TYPES.purchasePod) {
        setFunnelModalConfig({
          type: FUNNEL_TYPES.purchasePod,
          context: {
            ...config.context,
            modalContext: FunnelContextArea.featureCard,
          },
          options: {
            onSuccess: () => setFunnelModalConfig(),
          },
        });
        return;
      }
    }

    if (impressionId) {
      goToCTAUrlWithContext({ impressionId });
    } else {
      goToCTAUrl();
    }
  };

  const handleReleaseDetails = () => {
    const impressionId =
      section && sectionIndex !== undefined
        ? trackRecsysCardItemClick({
            section,
            sectionIndex,
            item: contentCard,
            itemIndex: index ?? 0,
          })
        : undefined;

    openReleaseInfoMenu(
      releaseData?.key ?? "",
      releaseData?.comic?.slug ?? "",
      applyNavigationAnalyticsAttribution(navigationContext, {
        impressionId,
      }),
    );
  };

  const { isUnlocked, isPremiumOnly, pricePointToUse } =
    useReleasePricingInfo(releaseData);
  const { user_read_status } = releaseData ?? {};

  const isALCRelease = !isComicCard && (isPremiumOnly || pricePointToUse);

  return (
    <Flex
      alignItems={releaseData || bundleData ? "end" : "center"}
      width="100%"
      mt="16px"
      gap="8px"
      justifyContent={!(isRoadblock && !isMobile) ? "space-between" : undefined}
      {...props}
    >
      {releaseData ? (
        <ReleasePricingActions
          contextArea={FunnelContextArea.featureCard}
          eventContextArea={EventContextArea.featureCard}
          release={releaseData}
          setFunnelConfig={handleButtonClick}
          hideFreeWithGold
          primaryButtonMaxWidth={buttonWidth}
          buttonSize="sm"
          escapeContainerBounds
        />
      ) : bundleData ? (
        <BundleButton
          bundle={bundleData}
          buttonWidth={buttonWidth}
          openBundleDetails={openBundleDetails}
          onTrackClick={() => {
            if (section && sectionIndex !== undefined) {
              trackRecsysCardItemClick({
                section,
                sectionIndex,
                item: contentCard,
                itemIndex: index ?? 0,
              });
            }
          }}
        />
      ) : (
        <StartContinueReadingButton
          userReadStatus={comicDataToUse?.user_read_status}
          isHidden={isHidden}
          handleButtonClick={handleButtonClick}
          buttonWidth={buttonWidth}
          isRoadblock={isRoadblock}
          isMobile={isMobile}
        />
      )}
      {isComicCard ? (
        <ReadingStatusButton
          cacheKey={`${comicDataToUse?.entity_type}-${comicDataToUse?.id}`}
          isSmall
          width="24px"
          useBookmark
        />
      ) : (
        releaseData && (
          <ReleaseCardMenu
            backgroundColor="transparent.white.10"
            margin="0 0 4px 0px"
            menuPlacement="top-end"
            iconSize="24px"
            onPreviewRelease={
              isUnlocked || (userIsGold && !isALCRelease)
                ? undefined
                : goToCTAUrl
            }
            shareUrl={releaseData.share_url}
            onStartReading={
              isUnlocked && !user_read_status ? goToCTAUrl : undefined
            }
            onContinueReading={
              isUnlocked && user_read_status ? goToCTAUrl : undefined
            }
            onReleaseDetails={handleReleaseDetails}
          />
        )
      )}
    </Flex>
  );
}

function getReadingStatusText(
  userReadStatus: ReadStatus | null | undefined,
  t: TFunction,
): string {
  if (userReadStatus) {
    return t("components.cards.customCard.buttonText.continueReading");
  } else {
    return t("components.cards.customCard.buttonText.startReading");
  }
}

interface StartContinueReadingButtonProps {
  userReadStatus: ReadStatus | null | undefined;
  isHidden: boolean;
  handleButtonClick: () => void;
  buttonWidth?: string | Record<string, string>;
  isRoadblock: boolean;
  isMobile: boolean;
}

function StartContinueReadingButton({
  userReadStatus,
  isHidden,
  handleButtonClick,
  buttonWidth,
  isRoadblock,
  isMobile,
}: StartContinueReadingButtonProps) {
  const { t } = useTranslation();

  return (
    <Button
      onClick={!isHidden ? handleButtonClick : undefined}
      variant="primary"
      size="md"
      width={
        buttonWidth ||
        (!(isRoadblock && !isMobile)
          ? "full"
          : { base: "200px", sm: "278px", md: "350px" })
      }
    >
      {getReadingStatusText(userReadStatus, t)}
    </Button>
  );
}

interface BundleButtonProps {
  bundle: Bundle;
  buttonWidth?: string | Record<string, string>;
  openBundleDetails: (bundleUuid: string) => void;
  onTrackClick?: () => void;
}

function BundleButton({
  bundle,
  buttonWidth,
  openBundleDetails,
  onTrackClick,
}: BundleButtonProps) {
  const { t } = useTranslation();

  const handleClick = () => {
    onTrackClick?.();

    if (bundle.uuid) {
      openBundleDetails(bundle.uuid);
    }
  };

  const buttonText = bundle.user_owns_all_items
    ? t("components.cards.customCard.buttonText.seeDetails")
    : t("components.cards.customCard.buttonText.buyNow");

  return (
    <Button
      onClick={handleClick}
      variant="primary"
      size="md"
      width={buttonWidth || "full"}
    >
      {buttonText}
    </Button>
  );
}
